import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import { DotText } from "@/v2-backoffice-client/components/dot"
import { getPaymentHealthConfig } from "@/v2-backoffice-client/customer/billing/customerBilling.constants"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  getRenewalContext,
  RENEWAL_EVENT_KIND_DISPLAY,
  RENEWAL_EVENT_KINDS,
  type RenewalContext,
  type RenewalEvent,
  type RenewalEventKind,
} from "@/v2-backoffice-client/license/renewals/license.renewals.utils"

// The table's row: the projected event flattened for filtering/grouping —
// every column a real field so pills, filters, grouping and export all work.
type RenewalEventRow = {
  Key: string
  Date: string
  Month: string
  CustomerName: string
  ExternalCustomerId: string | null
  PlanName: string
  PlanId: number | null
  Kind: RenewalEventKind
  Amount: number
  Mrr: number
  TermMonths: number
  Currency: string | null
  PaymentHealth: string | null
  Context: RenewalContext | null
}

/** The amount as it reads: churn leaves the book, so it counts negative. */
function signedAmount(event: RenewalEventRow) {
  return event.Kind === RENEWAL_EVENT_KINDS.CHURNING
    ? -event.Amount
    : event.Amount
}

function createRenewalEventColumns() {
  return createColumns<RenewalEventRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      Date: {
        id: "Date",
        label: "Date",
        defaultSize: 100,
        filter: { type: "date-filter" },
        renderCell({ row }) {
          return formatDateString(row.original.Date)
        },
        exportValue({ row }) {
          return formatDateString(row.original.Date)
        },
      },
      Month: {
        id: "Month",
        label: "Month",
        defaultSize: 80,
        defaultHidden: true,
        enableGrouping: true,
      },
      CustomerName: {
        id: "CustomerName",
        label: "Customer",
        defaultSize: 220,
        enableGrouping: true,
        to: ({ row }) =>
          row.original.ExternalCustomerId
            ? `>>?v=customer:${row.original.ExternalCustomerId}`
            : undefined,
      },
      PlanName: {
        id: "PlanName",
        label: "License",
        defaultSize: 180,
        enableGrouping: true,
        filter: { type: "text", placeholder: "License..." },
        to: ({ row }) =>
          row.original.PlanId != null
            ? `?v=plan:${row.original.PlanId}`
            : undefined,
      },
      Kind: {
        id: "Kind",
        label: "Event",
        defaultSize: 110,
        enableGrouping: true,
        renderCell({ row }) {
          const kind = row.original.Kind
          const display = RENEWAL_EVENT_KIND_DISPLAY[kind]
          return (
            <Badge
              variant={display.variant}
              tooltip={
                <div className="flex max-w-xs flex-col gap-1">
                  <p className="font-medium">{kind}</p>
                  <p className="text-muted-foreground">{display.description}</p>
                </div>
              }
            >
              {kind}
            </Badge>
          )
        },
      },
      // Why the event looks the way it does — the churn reason (Fenerum
      // records none, so it derives from our own domain: migration
      // conversations, license deprecation, payment trouble), and the same
      // markers on live renewals worth attention.
      Context: {
        id: "Context",
        label: "Context",
        defaultSize: 170,
        enableGrouping: true,
        getValue: (row) => row.Context?.label ?? "",
        renderCell({ row }) {
          const context = row.original.Context
          if (!context) return null
          return (
            <Badge
              variant={context.variant}
              size="xs"
              tooltip={
                <div className="flex max-w-xs flex-col gap-1">
                  <p className="font-medium">{context.label}</p>
                  <p className="text-muted-foreground">{context.description}</p>
                </div>
              }
            >
              {context.label}
            </Badge>
          )
        },
        exportValue({ row }) {
          return row.original.Context?.label ?? ""
        },
      },
      Amount: {
        id: "Amount",
        label: "Amount",
        description:
          "The cash of the event — the term invoice that lands, or for Churning the invoice that will NOT land.",
        defaultSize: 120,
        filter: { type: "number-range" },
        renderCell({ row }) {
          const { Amount, Currency, Kind } = row.original
          const isChurn = Kind === RENEWAL_EVENT_KINDS.CHURNING
          return (
            <span className={cn("tabular-nums", isChurn && "text-destructive")}>
              {isChurn ? "−" : ""}
              {formatInvoiceAmount(Amount, Currency)}
            </span>
          )
        },
        exportValue({ row }) {
          return signedAmount(row.original)
        },
        summaryValue({ row }) {
          return signedAmount(row.original)
        },
      },
      Mrr: {
        id: "Mrr",
        label: "MRR",
        defaultSize: 100,
        defaultHidden: true,
        filter: { type: "number-range" },
        renderCell({ row }) {
          return (
            <span className="tabular-nums">
              {formatInvoiceAmount(row.original.Mrr, row.original.Currency)}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Mrr
        },
      },
      TermMonths: {
        id: "TermMonths",
        label: "Term",
        defaultSize: 70,
        enableGrouping: true,
        getValue: (row) =>
          row.TermMonths === 12
            ? "Yearly"
            : row.TermMonths === 1
              ? "Monthly"
              : `${row.TermMonths} mo`,
      },
      PaymentHealth: {
        id: "PaymentHealth",
        label: "Payment",
        defaultSize: 100,
        defaultHidden: true,
        enableGrouping: true,
        getValue: (row) =>
          getPaymentHealthConfig(row.PaymentHealth)?.label ?? "",
        renderCell({ row }) {
          const config = getPaymentHealthConfig(row.original.PaymentHealth)
          if (!config) return null
          return <DotText variant={config.variant}>{config.label}</DotText>
        },
      },
    },
  })
}

export type LicenseRenewalTableProps = Omit<
  TableProps<RenewalEventRow>,
  "data" | "columns"
> & {
  events: RenewalEvent[]
  isLoading?: boolean
  error?: Error
  /** Chart-selected bucket's months; the pills' kind filter composes with it. */
  selectedMonthKeys: string[] | null
}

// The projected events as a worklist: one row per renewal / start / churn
// inside the horizon, filterable by kind (pills), month (chart click),
// license and amount — and groupable by customer for "who renews when".
function LicenseRenewalTable(props: LicenseRenewalTableProps) {
  const { className, events, isLoading, error, selectedMonthKeys, ...rest } =
    props

  const rows = useMemo<RenewalEventRow[]>(
    () =>
      events
        .filter(
          (event) =>
            selectedMonthKeys == null ||
            selectedMonthKeys.includes(event.monthKey),
        )
        .map((event) => ({
          Key: event.key,
          Date: event.date.toISOString(),
          Month: event.monthKey,
          CustomerName: event.item.CustomerName ?? `#${event.item.CustomerId}`,
          ExternalCustomerId: event.item.ExternalCustomerId ?? null,
          PlanName: event.item.PlanName ?? "",
          PlanId: event.item.PlanId ?? null,
          Kind: event.kind,
          Amount: event.amount,
          Mrr: event.item.Mrr,
          TermMonths: event.item.TermMonths,
          Currency: event.item.Currency ?? null,
          PaymentHealth: event.item.PaymentHealth ?? null,
          Context: getRenewalContext(event.item),
        })),
    [events, selectedMonthKeys],
  )

  const countByKind = useMemo(() => {
    const counts = new Map<RenewalEventKind, number>()
    for (const row of rows) {
      counts.set(row.Kind, (counts.get(row.Kind) ?? 0) + 1)
    }
    return counts
  }, [rows])

  const columns = useMemo(() => createRenewalEventColumns(), [])

  return (
    <Table
      className={cn("license-renewal-table", className)}
      tableId="license-renewal-table"
      data={rows}
      columns={columns}
      getRowId={(row) => row.Key}
      enableGrouping
      {...rest}
    >
      <Table.Toolbar>
        {/* Namespaced: the boards share the Licensing page, so the default
            "status" param would leak pill state between tabs. */}
        <Table.StatusFilter column="Kind" urlParam="renewals-status">
          <Table.StatusFilter.Option value={null} count={rows.length}>
            All
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={0}
            filterValue={RENEWAL_EVENT_KINDS.RENEWAL}
            variant="success"
            iconSrc="check-circle"
            count={countByKind.get(RENEWAL_EVENT_KINDS.RENEWAL) ?? 0}
          >
            Renewing
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={1}
            filterValue={RENEWAL_EVENT_KINDS.STARTING}
            count={countByKind.get(RENEWAL_EVENT_KINDS.STARTING) ?? 0}
          >
            Starting
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={2}
            filterValue={RENEWAL_EVENT_KINDS.AT_RISK}
            count={countByKind.get(RENEWAL_EVENT_KINDS.AT_RISK) ?? 0}
          >
            At risk
          </Table.StatusFilter.Option>

          <Table.StatusFilter.Option
            value={3}
            filterValue={RENEWAL_EVENT_KINDS.CHURNING}
            variant="destructive"
            iconSrc="exclamation"
            count={countByKind.get(RENEWAL_EVENT_KINDS.CHURNING) ?? 0}
          >
            Churning
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <div className="flex-1" />
        <Table.Search
          column="CustomerName"
          placeholder="Search by customer..."
        />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu exportFileName="renewals" />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.Content isLoading={isLoading} error={error} />
    </Table>
  )
}

export default LicenseRenewalTable
